import React, { useEffect, useState, useRef } from 'react';
import { getMyStartup, getDataroomDocuments, uploadDataroomDocument, deleteDataroomDocument, getDataroomDownloadUrl, updateDocStartupComment } from '../../api';
import { useToast } from '../../components/Toast';

// Taille max d'un document (doit rester cohérent avec le backend MAX_FILE_SIZE
// et client_max_body_size côté nginx).
const MAX_UPLOAD_MB = 25;

// Documents par thématique. Dans chaque thème, les obligatoires (status='required')
// sont placés en premier, puis les optionnels et conditionnels.
const CATEGORIES = {
  'BUSINESS_PLAN': {
    label: 'I. Business Plan (obligatoire pour le questionnaire QIT)',
    types: [
      { type: 'Business Plan', label: 'Business Plan (.pdf, .xlsx, .docx, .pptx) - obligatoire', status: 'required' }
    ]
  },
  'GENERAL': {
    label: 'II. Informations Générales',
    types: [
      // Obligatoires
      { type: 'Extrait Kbis (< 3 mois)', label: 'Extrait Kbis (< 3 mois) - obligatoire', status: 'required' },
      { type: 'Statuts à jour', label: 'Statuts à jour - obligatoire', status: 'required' },
      { type: 'CVs des fondateurs et dirigeants', label: 'CVs des fondateurs et dirigeants - obligatoire', status: 'required' },
      // Optionnels
      { type: 'Organigramme et actionnariat', label: 'Organigramme et actionnariat - Optionnel', status: 'optional' },
      { type: 'Liste des actionnaires et bénéficiaires effectifs', label: 'Liste des actionnaires et bénéficiaires effectifs - Optionnel', status: 'optional' },
      { type: 'PV des assemblées générales (3 derniers exercices)', label: 'PV des assemblées générales (3 derniers exercices) - Optionnel', status: 'optional' },
      { type: 'Pitch deck', label: 'Pitch deck - Optionnel', status: 'optional' }
    ]
  },
  'COMPTABILITE': {
    label: 'III. Comptabilité',
    types: [
      // Obligatoires
      { type: 'Comptes annuels (3 derniers exercices)', label: 'Comptes annuels (3 derniers exercices) - obligatoire', status: 'required' },
      // Conditionnels
      { type: 'Rapports des commissaires aux comptes', label: 'Rapports des commissaires aux comptes (si concerné)', status: null },
      // Optionnels
      { type: 'Fichier des écritures comptables (FEC)', label: 'Fichier des écritures comptables (FEC) - Optionnel', status: 'optional' },
      { type: 'Liasses fiscales (2033, 2050...)', label: 'Liasses fiscales - Optionnel', status: 'optional' },
      { type: "Rapports d'audit financier", label: "Rapports d'audit financier - Optionnel", status: 'optional' },
      { type: 'Comptes consolidés', label: 'Comptes consolidés - Optionnel', status: 'optional' }
    ]
  }
};

const ACCEPTED_TYPES = '.pdf,.xlsx,.xls,.docx,.doc,.jpg,.jpeg,.png';

const STATUS_MAP = {
  en_attente: { label: 'En attente de validation', color: '#F39C12' },
  valide: { label: 'Validé', color: '#27AE60' },
  rejete: { label: 'Rejeté', color: '#E74C3C' },
  a_completer: { label: 'À compléter', color: '#E67E22' }
};

export default function DataRoomPage() {
  const toast = useToast();
  const [startup, setStartup] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [docViewer, setDocViewer] = useState(null); // { doc, blobUrl, mimeType }
  const [startupComment, setStartupComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const load = async () => {
    const s = await getMyStartup();
    setStartup(s);
    const docs = await getDataroomDocuments();
    setDocuments(docs);
  };

  // Mandatory deposit lock — true when the QIT has been submitted for the
  // currently active campaign year. Consultation/download stay open ; only
  // upload, replace and delete are frozen until the candidate withdraws
  // their candidature from the « Mon dossier » page.
  const isDossierLocked = (() => {
    if (!startup) return false;
    const activeYear = startup.active_campaign_year;
    if (!activeYear) return false;
    const submittedStr = startup.qit_submitted_years || '';
    const submittedYears = submittedStr
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => parseInt(s, 10))
      .filter(n => !Number.isNaN(n));
    return submittedYears.includes(activeYear);
  })();

  useEffect(() => { load().catch(console.error); }, []);

  const handleUploadClick = (category, docType) => {
    if (uploading) return; // garde anti double-clic pendant un upload en cours
    if (isDossierLocked) {
      toast.error("Candidature soumise — le dépôt est verrouillé. Retirez votre candidature pour rouvrir l'édition.");
      return;
    }
    // Reset le file input AVANT de cliquer pour eviter qu un fichier
    // residuel d un upload precedent ne soit re-soumis silencieusement
    // ou que le navigateur ignore le click().
    if (fileInputRef.current) fileInputRef.current.value = '';
    setPendingUpload({ category, docType });
    // setTimeout 0 pour s assurer que pendingUpload est commit avant le click
    setTimeout(() => {
      if (fileInputRef.current) fileInputRef.current.click();
    }, 0);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Lire pendingUpload via le DOM si l etat React est stale (defensive)
    const pending = pendingUpload;
    if (!pending) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const { category, docType } = pending;
    // Contrôle de taille côté navigateur : message clair immédiat, on évite
    // un aller-retour serveur (et le 413 brut de nginx pour les très gros fichiers).
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo) — maximum ${MAX_UPLOAD_MB} Mo.`);
      setPendingUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(`${category}_${docType}`);
    try {
      await uploadDataroomDocument(file, category, docType);
      await load();
    } catch (err) {
      toast.error(err.message || 'Erreur lors du dépôt du document');
    } finally {
      setUploading(null);
      setPendingUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!startup) return;
    if (isDossierLocked) {
      toast.error("Candidature soumise — la suppression est verrouillée. Retirez votre candidature pour rouvrir l'édition.");
      return;
    }
    const ok = await toast.confirm('Supprimer ce document ?');
    if (!ok) return;
    try {
      await deleteDataroomDocument(docId);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('isi_token');
      const res = await fetch(getDataroomDownloadUrl(doc.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur de téléchargement');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openDocViewer = async (doc) => {
    try {
      const token = localStorage.getItem('isi_token');
      const res = await fetch(getDataroomDownloadUrl(doc.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur de téléchargement');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setDocViewer({ doc, blobUrl, mimeType: doc.mime_type || blob.type });
      setStartupComment(doc.startup_comment || '');
    } catch (err) { toast.error(err.message); }
  };

  const closeDocViewer = () => {
    if (docViewer?.blobUrl) window.URL.revokeObjectURL(docViewer.blobUrl);
    setDocViewer(null);
    setStartupComment('');
  };

  const handleSaveComment = async () => {
    if (!docViewer) return;
    setSavingComment(true);
    try {
      await updateDocStartupComment(docViewer.doc.id, startupComment);
      toast.success('Commentaire enregistré');
      await load();
      // Update the docViewer doc reference with the new comment
      setDocViewer(prev => prev ? { ...prev, doc: { ...prev.doc, startup_comment: startupComment } } : null);
    } catch (err) { toast.error(err.message); }
    setSavingComment(false);
  };

  if (!startup) return <div className="loading">Chargement...</div>;

  const getDocForType = (category, docType) => documents.find(d => d.category === category && d.document_type === docType);

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <h1 style={{ marginBottom: 8 }}>Data Room</h1>
        <p style={{ color: 'var(--text-light)', marginBottom: 32, fontSize: 14 }}>
          Déposez vos pièces justificatives dans le coffre-fort numérique sécurisé. Formats acceptés : PDF, XLSX, DOCX, JPG, PNG (max 25 Mo).
          Chaque document est archivé avec une empreinte SHA-256 conforme eIDAS.
        </p>

        {isDossierLocked && (
          <div style={{
            marginBottom: 24, padding: '14px 18px', borderRadius: 10,
            background: '#FFF8E1', border: '1px solid #FFE082',
            display: 'flex', alignItems: 'flex-start', gap: 12,
          }}>
            <span aria-hidden="true" style={{ fontSize: 22, lineHeight: 1, color: '#F57F17' }}>&#128274;</span>
            <div style={{ fontSize: 13.5, color: '#7A5A00', lineHeight: 1.55 }}>
              <strong style={{ color: '#5A4400' }}>Candidature soumise — dépôt verrouillé.</strong>
              {' '}
              Votre dossier est en cours d'étude pour les Trophées {startup.active_campaign_year}.
              Vous pouvez toujours <strong>consulter</strong> et <strong>télécharger</strong> les
              documents déjà déposés, mais le dépôt, le remplacement et la
              suppression sont gelés jusqu'à la fin de l'évaluation.
              <br />
              Pour rouvrir l'édition, retirez votre candidature depuis la page
              <em> « Mon dossier »</em> (cela effacera l'information de soumission).
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input type="file" ref={fileInputRef} style={{ display: 'none' }}
          accept={ACCEPTED_TYPES} onChange={handleFileSelected} />

        <div className="stats-grid" style={{ marginBottom: 32, gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--primary-light)' }}>{documents.length}</div>
            <div className="stat-label">Documents déposés</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#27AE60' }}>{documents.filter(d => d.status === 'valide').length}</div>
            <div className="stat-label">Validés</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: '#F39C12' }}>{documents.filter(d => d.status === 'en_attente').length}</div>
            <div className="stat-label">En attente de validation</div>
          </div>
        </div>

        {Object.entries(CATEGORIES).map(([catKey, cat]) => (
          <div key={catKey} className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 18, marginBottom: 20, color: 'var(--primary)' }}>{cat.label}</h2>
            <div style={{ display: 'grid', gap: 12 }}>
              {cat.types.map(item => {
                const docType = item.type;
                const labelStyle = item.status === 'required'
                  ? { fontSize: 14, fontWeight: 700, color: '#000' }
                  : item.status === 'optional'
                    ? { fontSize: 14, fontWeight: 500, color: '#555' }
                    : { fontSize: 14, fontWeight: 500 };
                const doc = getDocForType(catKey, docType);
                const isUploading = uploading === `${catKey}_${docType}`;
                return (
                  <div key={docType} style={{ padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 8,
                    background: doc ? '#FAFAFA' : 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={labelStyle}>{item.label}</div>
                        {doc && (
                          <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                            {doc.file_name} — {(doc.file_size / 1024).toFixed(0)} Ko
                            {doc.sha256_hash && (
                              <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 10, color: '#999' }}
                                title={`SHA-256: ${doc.sha256_hash}`}>
                                SHA: {doc.sha256_hash.substring(0, 12)}...
                              </span>
                            )}
                          </div>
                        )}
                        {doc && doc.status === 'rejete' && doc.rejection_reason && (
                          <div style={{ fontSize: 11, color: '#E74C3C', marginTop: 4 }}>
                            Motif : {doc.rejection_reason}
                          </div>
                        )}
                        {doc && doc.status === 'a_completer' && doc.rejection_reason && (
                          <div style={{ fontSize: 11, color: '#E67E22', marginTop: 4 }}>
                            Demande : {doc.rejection_reason}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {doc ? (
                          <>
                            <span className="badge" style={{ background: `${STATUS_MAP[doc.status]?.color}20`,
                              color: STATUS_MAP[doc.status]?.color, fontSize: 11 }}>
                              {STATUS_MAP[doc.status]?.label || doc.status}
                            </span>
                            <button onClick={() => openDocViewer(doc)}
                              style={{ padding: '4px 12px', border: '1px solid var(--primary)', borderRadius: 6,
                                background: '#F5F9FF', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                                color: 'var(--primary)', fontWeight: 600 }}>
                              Voir
                            </button>
                            {(doc.status === 'rejete' || doc.status === 'a_completer') && (
                              <button onClick={() => handleUploadClick(catKey, docType)}
                                className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }}
                                disabled={isUploading || isDossierLocked}
                                title={isDossierLocked ? 'Candidature soumise — dépôt verrouillé' : ''}>
                                {isUploading ? 'Envoi...' : 'Remplacer'}
                              </button>
                            )}
                            <button onClick={() => handleDelete(doc.id)}
                              disabled={isDossierLocked}
                              title={isDossierLocked ? 'Candidature soumise — suppression verrouillée' : 'Supprimer'}
                              style={{ background: 'none', border: 'none',
                                cursor: isDossierLocked ? 'not-allowed' : 'pointer',
                                color: isDossierLocked ? '#BDBDBD' : '#E74C3C', fontSize: 16,
                                opacity: isDossierLocked ? 0.6 : 1 }}>
                              {isDossierLocked ? '\u{1F512}' : '✕'}
                            </button>
                          </>
                        ) : (
                          <button onClick={() => handleUploadClick(catKey, docType)}
                            className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px' }}
                            disabled={isUploading || isDossierLocked}
                            title={isDossierLocked ? 'Candidature soumise — dépôt verrouillé' : ''}>
                            {isUploading ? 'Envoi...' : isDossierLocked ? '\u{1F512} Verrouillé' : 'Déposer'}
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Admin comment displayed below the document info */}
                    {doc && doc.admin_comment && (
                      <div style={{ marginTop: 8, padding: '8px 12px', background: '#FFF3E0', borderRadius: 6,
                        borderLeft: '3px solid #E67E22', fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: '#E65100' }}>Commentaire de l'analyste :</span>{' '}
                        <span style={{ color: '#333' }}>{doc.admin_comment}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Document Viewer Modal */}
        {docViewer && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={closeDocViewer}>
            <div style={{ background: 'white', borderRadius: 16, width: '92%', maxWidth: 1000, height: '88vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16 }}>{docViewer.doc.document_type}</h3>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                    {docViewer.doc.file_name} — {(docViewer.doc.file_size / 1024).toFixed(0)} Ko
                    <span className="badge" style={{ marginLeft: 8,
                      background: `${STATUS_MAP[docViewer.doc.status]?.color}20`,
                      color: STATUS_MAP[docViewer.doc.status]?.color, fontSize: 10 }}>
                      {STATUS_MAP[docViewer.doc.status]?.label || docViewer.doc.status}
                    </span>
                  </div>
                </div>
                <button onClick={closeDocViewer}
                  style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer',
                    color: 'var(--text-light)', padding: '4px 8px', lineHeight: 1 }}
                  title="Fermer">&times;</button>
              </div>

              {/* Document preview */}
              <div style={{ flex: 1, overflow: 'auto', background: '#F0F0F0' }}>
                {docViewer.mimeType?.startsWith('image/') ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24 }}>
                    <img src={docViewer.blobUrl} alt={docViewer.doc.file_name}
                      style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }} />
                  </div>
                ) : docViewer.mimeType === 'application/pdf' ? (
                  <iframe src={docViewer.blobUrl} title={docViewer.doc.file_name}
                    style={{ width: '100%', height: '100%', border: 'none' }} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    height: '100%', gap: 16, color: 'var(--text-light)' }}>
                    <div style={{ fontSize: 48 }}>📄</div>
                    <div style={{ fontSize: 14 }}>Aperçu non disponible pour ce type de fichier</div>
                    <button onClick={() => handleDownload(docViewer.doc)} className="btn btn-primary" style={{ fontSize: 13 }}>
                      Télécharger pour consulter
                    </button>
                  </div>
                )}
              </div>

              {/* Comments & Actions */}
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', background: '#FAFAFA', flexShrink: 0 }}>
                {/* Startup comment input */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)', display: 'block', marginBottom: 4 }}>
                      Votre commentaire (visible par l'analyste)
                    </label>
                    <textarea value={startupComment} onChange={e => setStartupComment(e.target.value)}
                      placeholder="Ajouter un commentaire sur ce document..."
                      rows={2}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #90CAF9',
                        fontFamily: 'inherit', fontSize: 12, resize: 'vertical', background: '#FAFEFF' }} />
                  </div>
                  <button onClick={handleSaveComment} disabled={savingComment}
                    style={{ padding: '8px 18px', borderRadius: 8, border: 'none',
                      background: 'var(--primary)', color: 'white', cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                      opacity: savingComment ? 0.6 : 1, marginBottom: 1 }}>
                    {savingComment ? 'Envoi...' : 'Enregistrer'}
                  </button>
                </div>

                {/* Admin comment (read-only) */}
                {docViewer.doc.admin_comment && (
                  <div style={{ marginTop: 10, padding: '8px 12px', background: '#FFF3E0', borderRadius: 8,
                    borderLeft: '3px solid #E67E22', fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: '#E65100' }}>Commentaire de l'analyste :</span>{' '}
                    <span style={{ color: '#333' }}>{docViewer.doc.admin_comment}</span>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleDownload(docViewer.doc)}
                    style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500 }}>
                    Télécharger
                  </button>
                  <button onClick={closeDocViewer}
                    style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'white', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500 }}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
